<template>
  <v-card :height="fullHeight" flat class="overflow-hidden">
    <v-speed-dial
      v-model="fab"
      direction="right"
      absolute
      left
      top
      open-on-hover
      transition="slide-x-transition"
    >
      <template #activator>
        <v-btn fab small class="toolbar-bg">
          <v-icon> mdi-eye </v-icon>
        </v-btn>
      </template>
      <template #default>
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-btn fab small class="toolbar-bg" v-on="on" @click="mode = 'map'">
              <v-icon>mdi-earth</v-icon>
            </v-btn>
          </template>
          <span>Map view</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-btn fab small class="toolbar-bg" v-on="on" @click="mode = 'diagram'">
              <v-icon>mdi-lan</v-icon>
            </v-btn>
          </template>
          <span>Diagram view</span>
        </v-tooltip>
      </template>
    </v-speed-dial>
    <!-- right drawer -->
    <!-- TODO: move case edit to a dedicated component -->
    <v-btn
      v-if="mode === 'diagram'"
      :elevation="showLabels ? 2 : 0"
      fab
      x-small
      class="toolbar-bg bottom-legend-toggle"
      right
      absolute
      :icon="!showLabels"
      @click="showLabels = !showLabels"
    >
      <v-icon>{{ showLabels ? "mdi-alphabetical" : "mdi-alphabetical-off" }}</v-icon>
    </v-btn>
    <v-fab-transition>
      <v-tooltip top>
        <template #activator="{ on }">
          <v-btn
            v-show="!drawer"
            fab
            small
            class="toolbar-bg bottom-plus"
            absolute
            right
            v-on="on"
            @click="plusClick"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Add item</span>
      </v-tooltip>
    </v-fab-transition>
    <Drawer v-model="drawer" :item.sync="formData" @cancel="cancelForm" @save="saveForm"></Drawer>

    <v-card height="100%" class="overflow-hidden" flat>
      <Diagram
        v-if="mode === 'diagram'"
        :current-case="currentCase"
        :use-nodes-coordinates="useNodesCoordinates"
        :show-labels="showLabels"
        @change="diagramChange"
        @update-breakers="diagramChange('breakers')"
      ></Diagram>
      <Map
        v-if="mode === 'map'"
        :current-case.sync="currentCase"
        :use-nodes-coordinates="useNodesCoordinates"
        :map-center.sync="mapCenter"
        :item.sync="formData"
        @change="$emit('change')"
        @update:node-positions="saveNodesPositions"
      ></Map>
    </v-card>

    <img src="@/assets/rex-logo.png" alt="Acelerex" class="logo logo-control" />
  </v-card>
</template>

<script>
import { CaseModel } from "data-dictionary";

export default {
  name: "CaseVisualization",
  components: {
    Diagram: () => import("@/components/VisDiagram.vue"),
    Drawer: () => import("@/components/VisDrawer.vue"),
    Map: () => import("@/components/GeographicalDiagram.vue"),
  },
  mixins: [],
  props: {
    currentCase: { type: Object, required: false, default: () => ({}) },
  },
  data() {
    return {
      mode: "diagram",
      fab: false,
      drawer: false,
      mapCenter: undefined,
      formData: {},
      showLabels: true,
    };
  },
  computed: {
    fullHeight() {
      const toolbar = 48;
      const visToolbarHeight = 36;
      const footer = 24;
      return this.$vuetify.breakpoint.height - visToolbarHeight - footer - toolbar;
    },
    useNodesCoordinates() {
      if (this.currentCase && this.currentCase.input.nodes.length > 1) {
        const [firstNode] = this.currentCase.input.nodes;
        return this.currentCase.input.nodes.some(
          (node) =>
            JSON.stringify(
              node.coordinates.map((v) => Math.round((Number(v) + Number.EPSILON) * 100) / 100)
            ) !==
            JSON.stringify(
              firstNode.coordinates.map((v) => Math.round((Number(v) + Number.EPSILON) * 100) / 100)
            )
        );
      }
      return false;
    },
  },
  watch: {
    useNodesCoordinates(v) {
      if (v) {
        this.mode = "map";
      }
    },
  },
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {
    if (this.useNodesCoordinates) {
      this.mode = "map";
    }
  },
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    saveNodesPositions(positionsArray) {
      const inputCopy = this.currentCase.input;
      const nodes = [];
      if (!inputCopy.nodes) return;
      inputCopy.nodes.forEach((node) => {
        const mapNode = positionsArray.find((v) => v.id === node.id);
        nodes.push({
          ...node,
          ...(mapNode ? { diagramPosition: { x: mapNode.x, y: mapNode.y } } : {}),
        });
      });
      inputCopy.nodes = nodes;
      this.currentCase.input = inputCopy;
      this.$emit("save");
    },
    diagramChange($e) {
      if ($e === "breakers") this.$emit("change");
      if (!this.useNodesCoordinates) this.$emit("change");
    },
    plusClick() {
      this.formData = new CaseModel.Node({ coordinates: Object.values(this.mapCenter) });
      this.drawer = true;
    },
    cancelForm() {
      this.formData = {};
      this.drawer = false;
    },
    saveForm() {
      const inputCopy = this.currentCase.input;
      const nextId = Math.max(...inputCopy.nodes.map((v) => v.id)) + 1;
      const newItem = { ...this.formData, id: nextId };
      inputCopy.nodes.push(newItem);
      this.drawer = false;
      this.$emit("change");
    },
  },
};
</script>

<style>
.floating-toolbar {
  z-index: 10;
  top: 8px;
  /* background-color: rgb(245 245 245 / 92%) !important; */
}
.toolbar-bg {
  background-color: rgb(245 245 245 / 90%) !important;
}
.v-btn.square-btn {
  height: 36px !important;
  width: 36px !important;
  min-width: 36px !important;
  padding: 0;
}
.bottom-plus {
  bottom: 40px;
}
.bottom-legend-toggle {
  bottom: 88px;
}

.logo-control {
  position: absolute;
  padding-bottom: 0px;
  text-align: end;
  margin-bottom: 0px !important;
  margin-right: 4px !important;
  bottom: 0px;
  right: 0;
}
.logo {
  max-height: 40px;
  opacity: 0.8;
}
</style>
